import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import { useState } from 'react';
import { useContactToggleTextSubscriptionMutation } from '../../../../../../../../queries/Contact';
import { Button } from '../../../../../../../components/Button';
import { Checkbox } from '../../../../../../../components/Checkbox';
import { InlineValueEditor } from '../../../../../../../components/entities/InlineValueEditor';
import { styleSheet } from '../../styles';
import { PhoneNumberCard } from './PhoneNumberCard';
import { PhoneNumberForm } from './PhoneNumberForm';

type Props = {
	contact: Api.IContact;
	contactVm: Api.ContactViewModel;
	onPhoneNumberChanged: (phoneNumbers: Api.IPhoneNumber[]) => void;
	refetchContact: () => void;
};

export const PhoneNumberList = ({ contact, contactVm, onPhoneNumberChanged, refetchContact }: Props) => {
	const [isAdding, setIsAdding] = useState(false);
	const [phoneNumberValue, setPhoneNumberValue] = useState<Api.IPhoneNumber | null>(null);

	const toggleTextSubscriptionMutation = useContactToggleTextSubscriptionMutation({
		onSuccess: () => {
			refetchContact();
		},
	});

	const onUnsubscribeFromTextCheckboxChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
		const checked = !!(e.target as HTMLInputElement).checked;

		// Use the React Query mutation instead of direct metadata change
		toggleTextSubscriptionMutation.mutate({
			contactId: contact.id,
			unsubscribe: checked,
		});
	};

	const onPhoneNumberUpdated = (newPhoneNumber: Api.IPhoneNumber, index: number) => {
		const newPhoneNumbers = [...(contact.phoneNumbers || [])];
		newPhoneNumbers[index] = newPhoneNumber;
		onPhoneNumberChanged(newPhoneNumbers);
	};

	const onDeletePhoneNumber = (index: number) => {
		const newPhoneNumbers = [...(contact.phoneNumbers || [])];
		newPhoneNumbers.splice(index, 1);
		onPhoneNumberChanged(newPhoneNumbers);
	};

	const onSaveNewPhoneNumber = () => {
		const newPhoneNumbers = [...(contact.phoneNumbers || [])];
		newPhoneNumbers.push(phoneNumberValue);
		onPhoneNumberChanged(newPhoneNumbers);
		setIsAdding(false);
		setPhoneNumberValue(null);
	};

	return (
		<div className={css(styleSheet.contactDetailSection)}>
			<Checkbox
				checked={Boolean(contact.metadata?.automatedSmsOptOutMetadata)}
				className={css(styleSheet.contactDetailUnsubscribeCheckbox)}
				id='text-unsubscribe-checkbox'
				onChange={onUnsubscribeFromTextCheckboxChanged}
				disabled={toggleTextSubscriptionMutation.isPending}
			>
				<span>Unsubscribe this contact from texting</span>
			</Checkbox>

			<div className={css(styleSheet.emailCardContainer)}>
				{contact.phoneNumbers?.map((phoneNumber, index) => (
					<PhoneNumberCard
						phoneNumber={phoneNumber}
						key={`${phoneNumber.value}-${index}`}
						contact={contactVm}
						onPhoneNumberChanged={(newPhoneNumber: Api.IPhoneNumber) => onPhoneNumberUpdated(newPhoneNumber, index)}
						isPrimary={index === 0}
						onDelete={() => onDeletePhoneNumber(index)}
					/>
				))}

				{isAdding ? (
					<InlineValueEditor
						autoComplete='off'
						autoFocus={true}
						compactLayout={true}
						id='entity-info-bullet-list-editor-input'
						onCancelButtonClicked={() => setIsAdding(false)}
						onKeyDown={ev => {
							if (ev.keyCode === 13) {
								onSaveNewPhoneNumber();
							}
						}}
						onRenderInput={() => <PhoneNumberForm phone={phoneNumberValue} onChange={setPhoneNumberValue} />}
						onSaveButtonClicked={onSaveNewPhoneNumber}
						value={phoneNumberValue.value || ''}
					/>
				) : (
					<div className={css(styleSheet.emailCardAddButton)}>
						<Button
							kind='link'
							onClick={() => {
								setIsAdding(true);
								setPhoneNumberValue({ value: '', label: '' });
							}}
						>
							+ Add Phone Number
						</Button>
					</div>
				)}
			</div>
		</div>
	);
};
